/**
 * gt-score.ts — the GT-eval SCORER for the gridline+marry pipeline.
 *
 * Epic: docs/epics/active/gridline-marry-refinement/main.md Phase 2 (Ph1b RATIFY).
 * Design: phase-1-design-260814.md §3 (the rubric) + phase-1b-review-260814.md
 * (§G-Q1 equal-weights-v1 + exact-role + F vector-only; §G-Q2 TS scorer-math reuse).
 *
 * WHY this module (single source of truth, A.9 / Ph1b §G-Q2). The scoring math —
 * IoU + the four-bbox-space reconciliation — MUST reuse `ground-truth-doc.ts`'s
 * `bboxIoU` + `pdfUserSpaceCornerToNorm`, never re-derive them: a second IoU /
 * reconciliation is the silent-wrong-for-months four-space hazard (INV-EVAL-2, held
 * by lint-single-bbox-iou-impl.py). This module adds ONLY the alignment (greedy
 * max-IoU matching) + the rubric on top of the reused oracle; even the band
 * containment metric is derived FROM `bboxIoU` (see `containmentOfExtract`) rather
 * than a fresh intersection primitive.
 *
 * PURE + browser-safe: no Node built-ins, no IO. The Node CLI wrapper
 * (`gt-score-cli.mjs`) does the file IO + the extraction-side reconciliation, then
 * calls `scoreGroundTruth`. The C# harness (GridlineMarryGtEvalHarnessTests) invokes
 * that CLI via the typed NodeGtScorerInvoker (INV-EVAL-9).
 *
 * @module editor/gt-score
 */
import { bboxIoU } from "./ground-truth-doc.js";
// ─── Rubric config (Ph1b REVISE-1 / INV-EVAL-10: versioned, separate lifecycle) ──
/**
 * Current scorer rubric version (semver). A bump invalidates every pinned per-doc S.
 *
 * 1.1.0 (RR-4 re-canary enabler): ADDITIVE — `BarScore.role` (exact-role agreement over
 * the BAR'S OWN matched-pair set) is now emitted on every `TwoBarReport` bar, via the
 * shared `roleAgreementOverPairs` helper. The strict-IoU `ScoreVector.role` value is
 * UNCHANGED (same formula, same matched set); this only surfaces the already-computed
 * containment/directional role scores that `scoreTwoBar` was discarding. See
 * `roleAgreementOverPairs` + `barFromResult`.
 */
export const RUBRIC_VERSION = "1.1.0";
export const DEFAULT_RUBRIC = {
    rubricVersion: RUBRIC_VERSION,
    weights: { detectionF1: 0.25, role: 0.25, geometry: 0.25, readingOrder: 0.25 },
    tauMatch: 0.5,
    wBand: 0.5,
    roleMatch: "exact",
};
// ─── Geometry helpers (all DERIVED from the reused bboxIoU — no 2nd primitive) ───
/**
 * Validate + clamp a box into the canonical [0,1] space (INV-EVAL-4). A hand-drawn GT
 * box can overflow the page canvas (the design's `gt-5.w = 1.409` quirk); left
 * unclamped it would yield an IoU > 1 or a negative union. NaN / non-finite inputs are
 * a real authoring fault → fail loud (A.14), never a silent 0.
 */
export function clampBbox(b) {
    for (const v of [b.x, b.y, b.w, b.h]) {
        if (!Number.isFinite(v)) {
            throw new Error(`gt-score: non-finite bbox component in ${JSON.stringify(b)}`);
        }
    }
    const x = Math.min(Math.max(b.x, 0), 1);
    const y = Math.min(Math.max(b.y, 0), 1);
    const w = Math.min(Math.max(b.w, 0), 1 - x);
    const h = Math.min(Math.max(b.h, 0), 1 - y);
    return { x, y, w, h };
}
function area(b) {
    return Math.max(0, b.w) * Math.max(0, b.h);
}
/**
 * Intersection area of two boxes, DERIVED from the reused `bboxIoU` (no second
 * intersection primitive — INV-EVAL-2). Algebra: IoU = I/(A+B−I) ⇒ I = IoU·(A+B)/(1+IoU).
 * The single site both containment directions reuse (DRY, A.10) so there is exactly one
 * intersection derivation in the module.
 */
function intersectionArea(a, b) {
    const iou = bboxIoU(a, b);
    if (iou <= 0)
        return 0;
    return (iou * (area(a) + area(b))) / (1 + iou);
}
/**
 * Fraction of the EXTRACTION box contained in the GT box (`area(g∩e)/area(e)`). A
 * correct, tighter extraction box therefore scores full containment against a loose
 * `band` GT box (§3.3), where raw IoU would wrongly penalize the better box. This is the
 * geometry sub-score `D`'s band metric — it PENALIZES an oversized extraction box (the
 * anti-gaming direction: a whole-page smear scores low here).
 */
export function containmentOfExtract(gt, ex) {
    const areaE = area(ex);
    if (areaE <= 0)
        return 0;
    return Math.min(1, intersectionArea(gt, ex) / areaE);
}
/**
 * Fraction of the GT box covered by the EXTRACTION box (`area(g∩e)/area(g)`) — the
 * OPPOSITE direction to `containmentOfExtract`. Answers "does the proposed region
 * SURROUND / cover the GT box's content?" — the "a wide band containing a tight paragraph
 * is good enough" criterion (Phase-6 metric study). NOTE the asymmetry: this is HIGH for a
 * whole-page box vs a small GT (that box covers everything), so it is gameable on its own —
 * the `directional` match mode pairs it with a centroid-locus gate, and the symmetric IoU
 * modes reject a whole-page smear outright. See phase-6-260814.md.
 */
export function coverageOfGt(gt, ex) {
    const areaG = area(gt);
    if (areaG <= 0)
        return 0;
    return Math.min(1, intersectionArea(gt, ex) / areaG);
}
/** Is the GT box's centroid inside the extraction box? (point-in-rect; not an IoU). */
function centroidInside(gt, ex) {
    const cx = gt.x + gt.w / 2;
    const cy = gt.y + gt.h / 2;
    return cx >= ex.x && cx <= ex.x + ex.w && cy >= ex.y && cy <= ex.y + ex.h;
}
/** The default eligibility floor per mode (§3.2/§3.3 candidate values + Phase-6 study). */
export function defaultThreshold(mode) {
    switch (mode) {
        case "strict-iou": return 0.5;
        case "relaxed-iou": return 0.3;
        case "containment": return 0.9;
        case "directional": return 0.7;
    }
}
export const STUDY_MODES = [
    { mode: "strict-iou", threshold: defaultThreshold("strict-iou") },
    { mode: "relaxed-iou", threshold: defaultThreshold("relaxed-iou") },
    { mode: "containment", threshold: defaultThreshold("containment") },
    { mode: "directional", threshold: defaultThreshold("directional") },
];
/**
 * Per-(gt,extract) eligibility + greedy-priority under a match mode. All derived from the
 * reused `bboxIoU` oracle (via `coverageOfGt`) + a point-in-rect centroid test — no second
 * intersection primitive (INV-EVAL-2).
 */
export function matchScore(cfg, gt, ex) {
    switch (cfg.mode) {
        case "strict-iou":
        case "relaxed-iou": {
            const iou = bboxIoU(gt, ex);
            return { eligible: iou >= cfg.threshold && iou > 0, strength: iou };
        }
        case "containment": {
            const cov = coverageOfGt(gt, ex);
            return { eligible: cov >= cfg.threshold && cov > 0, strength: cov };
        }
        case "directional": {
            const cov = coverageOfGt(gt, ex);
            return { eligible: centroidInside(gt, ex) && cov >= cfg.threshold, strength: cov };
        }
    }
}
/**
 * Greedy maximum-strength matching under a match mode. A pair (g,e) is eligible per
 * `matchScore`; pairs are consumed highest-strength-first, each GT + extraction element
 * used at most once. Matching is by GEOMETRY only — role does NOT gate a match (so the
 * role sub-score C stays meaningful), and ids are never compared (GT-INV-3 / INV-EVAL-1).
 * `MatchedPair.iou` always carries the true IoU regardless of the mode used to align, so
 * the geometry sub-score D and the disentanglement table read a consistent number.
 */
export function alignByMode(gt, extract, cfg) {
    const cands = [];
    for (const [gi, g] of gt.entries()) {
        const gb = clampBbox(g.bbox);
        for (const [ei, e] of extract.entries()) {
            // INV-EVAL-11: page-partitioned matching — a candidate pair is only
            // generated when GT and extract share a page. Without this, per-page
            // normalized [0,1] boxes from DIFFERENT pages spuriously overlap in the
            // pooled comparison frame (Fable phase-5-fable-judgment-260913.md F3).
            if (g.page !== e.page)
                continue;
            const eb = clampBbox(e.bbox);
            const { eligible, strength } = matchScore(cfg, gb, eb);
            if (eligible)
                cands.push({ gi, ei, iou: bboxIoU(gb, eb), strength });
        }
    }
    cands.sort((a, b) => b.strength - a.strength);
    const usedGt = new Set();
    const usedEx = new Set();
    const matched = [];
    for (const c of cands) {
        if (usedGt.has(c.gi) || usedEx.has(c.ei))
            continue;
        usedGt.add(c.gi);
        usedEx.add(c.ei);
        const g = gt[c.gi];
        const e = extract[c.ei];
        if (!g || !e)
            continue;
        matched.push({ gtId: g.gtId, extractId: e.id, iou: c.iou, roleMatch: g.role === e.role });
    }
    const missedGt = [];
    for (const [gi, g] of gt.entries())
        if (!usedGt.has(gi))
            missedGt.push(g.gtId);
    const spuriousExtract = [];
    for (const [ei, e] of extract.entries())
        if (!usedEx.has(ei))
            spuriousExtract.push(e.id);
    return { matched, missedGt, spuriousExtract };
}
/**
 * Greedy max-IoU matching at threshold `tau` — the default scorer alignment (strict-IoU).
 * Thin wrapper over `alignByMode` so there is exactly ONE greedy-matching implementation
 * (A.10). Behaviour is byte-identical to the pre-Phase-6 strict path.
 */
export function alignByIoU(gt, extract, tau) {
    return alignByMode(gt, extract, { mode: "strict-iou", threshold: tau });
}
// ─── Reading-order agreement (E): 1 − normalized inversions ──────────────────────
function readingOrderScore(matched, gtOrder, exOrder) {
    const n = matched.length;
    // < 2 matched pairs → reading order is UNDEFINED, not "perfect". Return null
    // (N/A) rather than the old trivial 1 so the caller drops E from the scalar
    // (mirrors F/INV-EVAL-5). The old `return 1` floored a total-detection-failure
    // at S=0.25 under equal weights — a misleading "25% good" for 0/N matched
    // (davis phase-3 baseline). N/A-ing E lets S fall toward 0 (phase-3 §5 R-E).
    if (n < 2)
        return null;
    // Sort matched pairs by GT reading order, read off the extraction order, count inversions.
    const seq = matched
        .map((m) => ({ g: gtOrder.get(m.gtId) ?? 0, e: exOrder.get(m.extractId) ?? 0 }))
        .sort((a, b) => a.g - b.g)
        .map((p) => p.e);
    let inversions = 0;
    for (let i = 0; i < seq.length; i++) {
        for (let j = i + 1; j < seq.length; j++) {
            const a = seq[i];
            const b = seq[j];
            if (a !== undefined && b !== undefined && a > b)
                inversions++;
        }
    }
    const maxInversions = (n * (n - 1)) / 2;
    return maxInversions === 0 ? 1 : 1 - inversions / maxInversions;
}
/**
 * C — exact-role agreement over ANY matched-pair set. Fraction of `pairs` whose
 * `roleMatch` (set at alignment time in `alignByMode`, `g.role === e.role`) is true; 0
 * when `pairs` is empty (mirrors the pre-1.1 inline calc — not N/A, since an empty
 * detection result is a real 0, not an undefined axis). Parameterized by the matched-pair
 * LIST (not a match mode) so the identical formula applies to the strict-IoU set (the
 * existing `ScoreVector.role`, UNCHANGED) and — new in rubric 1.1 — the containment and
 * directional matched sets (RR-4 de-noised role-fidelity; A.10 DRY, one role-agreement
 * computation site).
 */
export function roleAgreementOverPairs(pairs) {
    return pairs.length > 0 ? pairs.filter((p) => p.roleMatch).length / pairs.length : 0;
}
// ─── The rubric ───────────────────────────────────────────────────────────────
/**
 * Score a pipeline's extraction against a GroundTruthDoc. Returns the (B,C,D,E[,F])
 * vector — the refinement compass (A.3 measure-one-deeper) — plus the weighted scalar
 * `S` over (B,C,D) + E-when-matched>=2 that gates baseline drift. E (reading order) is
 * `null` (N/A) and dropped from the scalar when fewer than 2 pairs match, and F
 * (nesting) is vector-only and is `null` (N/A) for a flat target (INV-EVAL-5) — neither
 * a vacuous 1 nor a penalizing 0 when undefined.
 */
export function scoreGroundTruth(gtDoc, extract, rubric = DEFAULT_RUBRIC, matchMode) {
    const gt = gtDoc.elements;
    // Default (matchMode undefined): strict-IoU at rubric.tauMatch — the pinned scorer
    // path, byte-identical to pre-Phase-6. A matchMode override re-aligns under a study
    // criterion (containment / relaxed / directional) WITHOUT changing the sub-score math:
    // the geometry sub-score D still reads the true IoU on matched tight pairs, so the
    // reported scalar honestly reflects the composite under that alignment.
    const { matched, missedGt, spuriousExtract } = matchMode
        ? alignByMode(gt, extract, matchMode)
        : alignByIoU(gt, extract, rubric.tauMatch);
    // B — detection F1.
    const nMatched = matched.length;
    const precision = extract.length > 0 ? nMatched / extract.length : 0;
    const recall = gt.length > 0 ? nMatched / gt.length : 0;
    const detectionF1 = precision + recall > 0 ? (2 * precision * recall) / (precision + recall) : 0;
    // C — exact-role agreement over matched pairs (DRY: shared `roleAgreementOverPairs`,
    // A.10 — the same formula also drives the containment/directional two-bar role fields).
    const role = roleAgreementOverPairs(matched);
    // D — confidence-weighted geometry (tight = strict IoU; band = containment).
    const gtById = new Map(gt.map((g) => [g.gtId, g]));
    const exById = new Map(extract.map((e) => [e.id, e]));
    let dNum = 0;
    let dDen = 0;
    for (const m of matched) {
        const g = gtById.get(m.gtId);
        const e = exById.get(m.extractId);
        if (!g || !e)
            continue;
        const isBand = g.geometryConfidence === "band";
        const w = isBand ? rubric.wBand : 1;
        const geomScore = isBand
            ? containmentOfExtract(clampBbox(g.bbox), clampBbox(e.bbox))
            : m.iou;
        dNum += w * geomScore;
        dDen += w;
    }
    const geometry = dDen > 0 ? dNum / dDen : 0;
    // E — reading-order agreement.
    const gtOrder = new Map(gt.map((g) => [g.gtId, g.readingOrderIndex]));
    const exOrder = new Map(extract.map((e) => [e.id, e.readingOrderIndex]));
    const readingOrder = readingOrderScore(matched, gtOrder, exOrder);
    // F — nesting (parentage correctness); N/A (null) for a flat target (INV-EVAL-5).
    const nesting = computeNesting(gt, matched, exById);
    // Weighted scalar over (B,C,D) always, plus E ONLY when it is non-null
    // (matched >= 2). When E is N/A, its weight is dropped from BOTH the numerator
    // AND the denominator (renormalize over the present axes) — so a
    // total-detection-failure scores S -> 0 rather than the 0.25 vacuous floor the
    // always-1 reading-order trivial-pass produced (phase-3 §5 R-E; F/INV-EVAL-5
    // analogue). F (nesting) stays vector-only, never in the scalar (§G-Q1).
    let sNum = rubric.weights.detectionF1 * detectionF1 +
        rubric.weights.role * role +
        rubric.weights.geometry * geometry;
    let wsum = rubric.weights.detectionF1 +
        rubric.weights.role +
        rubric.weights.geometry;
    if (readingOrder !== null) {
        sNum += rubric.weights.readingOrder * readingOrder;
        wsum += rubric.weights.readingOrder;
    }
    const scalar = wsum > 0 ? sNum / wsum : 0;
    return {
        rubricVersion: rubric.rubricVersion,
        scalar,
        vector: { detectionF1, role, geometry, readingOrder, nesting },
        precision,
        recall,
        matched,
        missedGt,
        spuriousExtract,
    };
}
/**
 * F — parentage correctness (§3.4). For every GT element with a `parentGtId`, the
 * nesting is correct iff the child's matched extraction element's `parentId` equals the
 * extraction element matched to the GT parent. Score = correct / total-nested-GT-children.
 * Returns null (N/A) when the target has no nesting at all — a flat target must NOT be
 * penalized for a list it does not contain (INV-EVAL-5).
 */
function computeNesting(gt, matched, exById) {
    const nestedChildren = gt.filter((g) => g.parentGtId !== null);
    if (nestedChildren.length === 0)
        return null;
    const exIdByGtId = new Map(matched.map((m) => [m.gtId, m.extractId]));
    let correct = 0;
    for (const child of nestedChildren) {
        const childExId = exIdByGtId.get(child.gtId);
        const parentExId = child.parentGtId ? exIdByGtId.get(child.parentGtId) : undefined;
        if (childExId === undefined || parentExId === undefined)
            continue; // unmatched → not correct
        const childEx = exById.get(childExId);
        if (childEx && childEx.parentId === parentExId)
            correct++;
    }
    return correct / nestedChildren.length;
}
// ─── TWO-BAR metric (pdf-quality Phase 2, user ruling G-2) ───────────────────────
//
// The composite scalar `S` (scoreGroundTruth) CONFLATES placement and tightness into one
// number and thereby MISRANKS: it put mitnotes #1 by S=0.665 with detF1 0.033 (a 96%
// detection miss — corpus-readout-260912.md:140). The user ruling (G-2, ratified 260923)
// is a TWO-BAR metric reported as a PAIR and NEVER composited — so one bar can be improved
// while the other is watched for regression. This promotes the already-built matchMode seam
// (phase-1b-review-260923.md R-3: containment/directional already implemented above) into
// first-class two-bar reporting; it does NOT rebuild alignment machinery.
//
//   - PLACEMENT bar — detF1/precision/recall under `containment` (the wide-band-that-
//     surrounds-the-GT criterion), read ALONGSIDE `directional` to bound smear-gaming
//     (a whole-page box passes containment but the directional centroid-gate exposes it).
//   - TIGHTNESS bar — detF1 under the pinned `strict-iou` 0.5 path (byte-identical to the
//     default scorer alignment) + mean matched IoU.
//
// `scalar` (S) is RETAINED on the ScoreResult for regression comparability but is DEMOTED:
// it is no longer the headline/ranking number — ranking uses the PLACEMENT bar's
// detF1/recall (G-2b). The bar vocabulary is the SHARED join named once in
// `test/gt-corpus/two-bar-vocabulary.json` and pinned cross-stack (vs tools/ir_ladder/
// scorer.py) by `tools/ir_ladder/test_two_bar_parity.py` (R-4, A.24 second-surface CONTROL).
//
// RUBRIC 1.1 (RR-4 re-canary enabler, ADDITIVE): `BarScore.role` surfaces the exact-role
// agreement over EACH bar's own matched-pair set (`roleAgreementOverPairs`, shared with the
// strict-IoU `ScoreVector.role`). The strict-IoU-only `vector.role` is noisy when strict
// alignment matches very few pairs (e.g. davis 2/22 GT elements → 0/2 role); the
// containment/directional matched sets are typically larger, so `placement.role` /
// `placement.directional.role` give a de-noised role-fidelity reading alongside the existing
// detection fields — read, like the rest of the two-bar metric, as a PAIR per bar, never
// composited into `scalar`.
/**
 * Shared two-bar vocabulary version (matches `two-bar-vocabulary.json.vocabularyVersion`).
 * A bump signals a change to the bar DEFINITIONS the parity pin holds across both stacks.
 */
export const TWO_BAR_VOCABULARY_VERSION = "1.0.0";
function barFromResult(r) {
    return {
        detectionF1: r.vector.detectionF1,
        precision: r.precision,
        recall: r.recall,
        matchedCount: r.matched.length,
        // rubric 1.1: r.vector.role is ALREADY computed over r's own matched set (the
        // containment/directional/strict-IoU alignment the caller scored under) via the
        // shared roleAgreementOverPairs helper — no re-derivation, just surfacing it.
        role: r.vector.role,
    };
}
/**
 * Score a pipeline's extraction on the TWO-BAR metric (G-2). Reuses `scoreGroundTruth`
 * under three match modes (no second matcher — A.10 / INV-EVAL-2): `containment` for the
 * placement headline, `directional` alongside for the anti-gaming bound, and the pinned
 * default `strict-iou` path for tightness. The two bars are returned as a PAIR and are NEVER
 * composited — the caller ranks by `placement.detectionF1` / `placement.recall`, watching
 * `tightness` for regression. Only the detection sub-metrics of each mode's result are
 * consumed (`.vector.detectionF1`, `.precision`, `.recall`, `.matched`) — never the
 * composite `.scalar`, so no compositing leaks in.
 */
export function scoreTwoBar(gtDoc, extract, rubric = DEFAULT_RUBRIC) {
    const containment = scoreGroundTruth(gtDoc, extract, rubric, {
        mode: "containment",
        threshold: defaultThreshold("containment"),
    });
    const directional = scoreGroundTruth(gtDoc, extract, rubric, {
        mode: "directional",
        threshold: defaultThreshold("directional"),
    });
    // Tightness uses the DEFAULT (matchMode undefined) path — byte-identical to the pinned
    // pre-Phase-6 strict-IoU alignment (scoreGroundTruth §"Default").
    const strict = scoreGroundTruth(gtDoc, extract, rubric);
    const matchedIoU = strict.matched.length > 0
        ? strict.matched.reduce((s, m) => s + m.iou, 0) / strict.matched.length
        : 0;
    return {
        vocabularyVersion: TWO_BAR_VOCABULARY_VERSION,
        rubricVersion: rubric.rubricVersion,
        gtCount: gtDoc.elements.length,
        extractCount: extract.length,
        placement: { mode: "containment", ...barFromResult(containment), directional: barFromResult(directional) },
        tightness: { mode: "strict-iou", ...barFromResult(strict), matchedIoU },
    };
}
// ─── Phase-6 metric study: the disentanglement instrument ─────────────────────────
//
// The davis baseline S=0.5715 (2/14 matched at strict IoU≥0.5) CONFLATES two failures:
// (a) algorithm quality (the pipeline never produced a region for a GT — a detection
// recall gap) vs (b) metric strictness (the pipeline DID produce a usable region but a
// symmetric IoU floor penalized a band that is legitimately wider than a tight GT text
// box). This instrument re-scores ONE recorded extract under the four `MatchMode`s and,
// per GT element, reports the RAW geometric relation to every extract box so the two
// failures can be separated. It is a pure re-scoring of the recorded pipeline output —
// it changes NOTHING about the pipeline (Phase-6 is metric-only).
/**
 * At/above this normalized AREA, an extraction box is treated as a whole-page SMEAR (e.g.
 * the marry whole-page Figure fallback), NOT a dedicated region. A box covering ≥ half the
 * page cannot be a bounded region for a paragraph. This — not a per-GT containment ratio —
 * is the clean discriminator between a legitimately-wide COLUMN BAND (bounded, area ≪ page)
 * and a page smear (area → 1): a tall thin word GT is <6% of a column band, which a
 * containment ratio would wrongly flag as a smear even though the band is a real region.
 */
export const SMEAR_AREA_FLOOR = 0.5;
/** GT-coverage at/above which a GT counts as geometrically "captured" by some box. */
export const DETECTION_COVERAGE_FLOOR = 0.9;
const ALL_MODES = ["strict-iou", "relaxed-iou", "containment", "directional"];
function emptyModeFlags() {
    return { "strict-iou": false, "relaxed-iou": false, containment: false, directional: false };
}
/**
 * Re-score a recorded extract under every `MatchMode` and build the per-GT disentanglement
 * table. Pure; no IO. The classification uses RAW eligibility (does a suitable box EXIST)
 * for the detected-vs-missed question, while the mode summaries use the contention-aware
 * greedy alignment (what the scorer would actually report) — so the report shows both "was
 * it detectable" and "did it survive 1:1 assignment".
 */
export function disentangle(gtDoc, extract, modes = STUDY_MODES, rubric = DEFAULT_RUBRIC) {
    const gt = gtDoc.elements;
    // Greedy alignment per mode → per-mode matched sets + summaries.
    const greedyMatchedGtByMode = new Map();
    const modeSummaries = [];
    for (const cfg of modes) {
        const result = scoreGroundTruth(gtDoc, extract, rubric, cfg);
        greedyMatchedGtByMode.set(cfg.mode, new Set(result.matched.map((m) => m.gtId)));
        modeSummaries.push({
            mode: cfg.mode,
            threshold: cfg.threshold,
            matchedCount: result.matched.length,
            recall: result.recall,
            scalar: result.scalar,
        });
    }
    const thresholdByMode = new Map(modes.map((m) => [m.mode, m.threshold]));
    const rows = [];
    const classificationCounts = {
        "matched-strict": 0,
        "detected-band-penalized": 0,
        "detected-smear-only": 0,
        missed: 0,
    };
    for (const g of gt) {
        const gb = clampBbox(g.bbox);
        // Raw per-GT scan over ALL extract boxes (ignores greedy contention).
        let bestIoU = 0;
        let bestCoverageOfGt = 0;
        let bestCoverExtractId = null;
        let bestCoverContainment = 0;
        const rawEligible = emptyModeFlags();
        const rawEligibleBounded = emptyModeFlags();
        for (const e of extract) {
            // INV-EVAL-11: page-partitioned matching (see alignByMode) — the raw
            // per-GT scan must not consider extract boxes from a different page,
            // or the disentanglement classification stays page-blind even though
            // the greedy alignment above it is now page-partitioned.
            if (g.page !== e.page)
                continue;
            const eb = clampBbox(e.bbox);
            const isSmear = area(eb) >= SMEAR_AREA_FLOOR;
            const iou = bboxIoU(gb, eb);
            if (iou > bestIoU)
                bestIoU = iou;
            const cov = coverageOfGt(gb, eb);
            if (cov > bestCoverageOfGt) {
                bestCoverageOfGt = cov;
                bestCoverExtractId = e.id;
                bestCoverContainment = containmentOfExtract(gb, eb);
            }
            for (const mode of ALL_MODES) {
                const cfg = { mode, threshold: thresholdByMode.get(mode) ?? defaultThreshold(mode) };
                if (!matchScore(cfg, gb, eb).eligible)
                    continue;
                rawEligible[mode] = true;
                if (!isSmear)
                    rawEligibleBounded[mode] = true;
            }
        }
        const matchedUnder = emptyModeFlags();
        for (const mode of ALL_MODES) {
            matchedUnder[mode] = greedyMatchedGtByMode.get(mode)?.has(g.gtId) ?? false;
        }
        // Classification (raw-eligibility based; see GtClassification doc). A BOUNDED region
        // eligible under any relaxed criterion ⇒ the pipeline produced a usable region that
        // strict IoU penalized (metric). Otherwise, if a box still covers the GT it can only
        // be a whole-page smear (algorithm produced no dedicated region); else nothing at all.
        let classification;
        if (matchedUnder["strict-iou"]) {
            classification = "matched-strict";
        }
        else if (rawEligibleBounded["relaxed-iou"] ||
            rawEligibleBounded.containment ||
            rawEligibleBounded.directional) {
            classification = "detected-band-penalized";
        }
        else if (bestCoverageOfGt >= DETECTION_COVERAGE_FLOOR) {
            classification = "detected-smear-only";
        }
        else {
            classification = "missed";
        }
        classificationCounts[classification]++;
        rows.push({
            gtId: g.gtId,
            role: g.role,
            geometryConfidence: g.geometryConfidence,
            bestIoU,
            bestCoverageOfGt,
            bestCoverExtractId,
            bestCoverContainment,
            rawEligible,
            rawEligibleBounded,
            matchedUnder,
            classification,
        });
    }
    const strictUnmatched = {
        total: classificationCounts["detected-band-penalized"] +
            classificationCounts["detected-smear-only"] +
            classificationCounts.missed,
        detectedBandPenalized: classificationCounts["detected-band-penalized"],
        detectedSmearOnly: classificationCounts["detected-smear-only"],
        missed: classificationCounts.missed,
    };
    return {
        rubricVersion: rubric.rubricVersion,
        gtCount: gt.length,
        extractCount: extract.length,
        modes: modeSummaries,
        rows,
        classificationCounts,
        strictUnmatched,
    };
}
/** Thresholds from phase-8-semantic-first-measurement-260923.md §0 (pinned, do not drift). */
export function coverageBand(bestCoverageOfGt) {
    if (bestCoverageOfGt >= 0.9)
        return "COVERED";
    if (bestCoverageOfGt >= 0.5)
        return "PARTIAL";
    if (bestCoverageOfGt >= 0.1)
        return "LOW";
    return "MISSING";
}
/** Derive the Phase-8 coverage-RCA from an already-computed `DisentangleReport`. */
export function summarizeCoverageRca(report) {
    const bandCounts = { COVERED: 0, PARTIAL: 0, LOW: 0, MISSING: 0 };
    const rows = report.rows.map((r) => {
        const band = coverageBand(r.bestCoverageOfGt);
        bandCounts[band]++;
        return { gtId: r.gtId, role: r.role, bestCoverageOfGt: r.bestCoverageOfGt, band };
    });
    const realMissList = rows.filter((r) => r.band === "LOW" || r.band === "MISSING");
    const contentCov = report.gtCount > 0 ? (bandCounts.COVERED + bandCounts.PARTIAL) / report.gtCount : 1;
    return { contentCov, bandCounts, realMiss: { count: realMissList.length, list: realMissList }, rows };
}
